<template>
  <div
    :class="{
      'px-4': app.isTeemill && mobile,
    }"
  >
    <template v-if="!loading">
      <div class="blog-post">
        <display-post
          v-if="post"
          :post="post"
          :comment-count="commentCount"
          @click-comment-count="goToCommentsSection"
        />
      </div>

      <div class="footer">
        <div v-if="!activeCustomer && app.isTeemill">
          <hr />

          <component
            :is="mobile ? 'tml-compact-banner' : 'tml-slim-banner'"
            :image="RegisterBanner"
            vertical-alignment="top"
            button-size="small"
            font="Poppins"
            title-color="#2c2d2d"
            copy-color="#2c2d2d"
            title="Get Started"
            copy="Build your free store with Teemill"
            :actions="[
              {
                text: 'Sign Up',
                href: '/register/',
                primary: false,
              },
            ]"
            :overlay-opacity="0"
            text-shadow=""
            button-border-radius="4px"
            :min-height="mobile ? '400px' : '550px'"
            lazy-load
          />

          <hr />
        </div>

        <template v-if="post && post.similarReads.length">
          <h4>Similar Reads</h4>
          <tml-grid
            :breakpoints="{
              xs: 2,
              lg: 3,
            }"
            spacing="0.5em"
            :ratio="0.75"
          >
            <template v-for="similarRead in displayedSimilarReads">
              <tml-new-pod
                v-if="similarRead.id !== post.id"
                :key="similarRead.id"
                :title="similarRead.title"
                :href="similarRead.url"
                :image="similarRead.image.size('thumbnail')"
                :ratio="3 / 4"
              />
            </template>
          </tml-grid>
        </template>

        <template v-if="showComments">
          <hr />

          <post-comments
            id="comments"
            :post="commentablePost"
            :user="activeCustomer"
          />
        </template>
      </div>
    </template>

    <div ref="endOfPostMarker" class="w-full" />
  </div>
</template>

<script>
import {configureScope, captureException} from '@sentry/vue';

import take from 'lodash/take';

import {escapeJSON} from '@teemill/utilities';
import {formatUrl, scrollToElement, maxDevice} from '@teemill/common/helpers';
import {ApiError, NotFoundError} from '@teemill/common/errors';
import {snackbar} from '@teemill/common/services';

import {User} from '@teemill/common/models';
import {ChatMessageModel} from '@teemill/modules/chat';
import data from './data-mixin.js';
import DisplayPost from './partials/DisplayPost.vue';
import RegisterBanner from '../../assets/img/register-banner-img.png';
import {computed, defineAsyncComponent} from 'vue';
import {useHead} from '@vueuse/head';

import Blog from '../../../classes/blog.js';
import isbot from 'isbot';

export default {
  name: 'BlogPostPage',

  components: {
    DisplayPost,
    PostComments: defineAsyncComponent(() =>
      import('./partials/PostComments.vue')
    ),
  },

  mixins: [data],

  inject: ['app'],

  beforeRouteLeave(to, from, next) {
    if (this.timer) {
      clearTimeout(this.timer);
    }

    next();
  },

  data() {
    return {
      meta: {
        indexPage: true,
      },

      User,

      timer: null,
      loading: true,
      scrolledToBottom: false,
      intersectionObserver: null,
      twentySecondsElapsed: false,

      commentablePost: null,
      showComments: false,
      markupComments: null,
      RegisterBanner,
    };
  },

  computed: {
    authorDescription() {
      let string = 'Author. ';

      if (this.post && this.post.editors && this.post.author) {
        /**
         * A blog author will also always be its first editor due to how the
         * components work, so if a post only has one editor we can assume that it's
         * the author and therefore don't need to show a list of editors
         */
        if (this.post.editors.length === 1) {
          return string;
        }

        string += 'Edited by ';

        Object.values(this.post.editors).forEach(editor => {
          if (editor.id !== this.post.author.id) {
            string += `${editor.firstName}, `;
          }
        });
      }

      // If last character is a comma, remove it
      return string.replace(/,\s*$/, '');
    },

    commentCount() {
      if (this.commentablePost) {
        if (this.app.isTeemill) {
          return this.commentablePost.getVisibleCommentCount(
            this.$store.state.user
          );
        } else if (this.app.isAtlas) {
          return this.commentablePost.commentCount;
        }
      }

      return null;
    },

    displayedSimilarReads() {
      return this.mobile
        ? take(this.post.similarReads, 2)
        : this.post.similarReads;
    },

    activeCustomer() {
      return $store.state.user;
    },

    teemillMetaLogo() {
      return this.$store.state?.logo?.url || '';
    },

    mobile() {
      return maxDevice('md');
    },

    title() {
      return `${this.pageTitle(this.$route)} | Blog`;
    },
  },

  watch: {
    '$route.params.post': {
      immediate: true,
      handler(post, oldPost) {
        if (post && post !== oldPost) {
          this.loading = true;

          const {params} = this.$route;

          this.fetchPost(params.post)
            .then(() => {
              if (this.post) {
                this.timer = setTimeout(() => {
                  this.twentySecondsElapsed = true;
                  this.markBlogAsRead();
                }, 20000);

                if (!isbot(navigator.userAgent)) {
                  this.$store.dispatch('blog/logPostView', this.post);
                }

                if (this.app.isAtlas || this.app.isTeemill) {
                  this.showComments = true;
                  this.loadComments();
                }
              }
            })
            .catch(error => {
              if (error instanceof NotFoundError) {
                this.$router.push('/blog/?notFound=true');
                snackbar.error("Sorry, we couldn't find that post");
                return;
              }

              if (error instanceof ApiError) {
                snackbar.error();
                configureScope(scope => {
                  scope.setTag('post_url', post);
                  scope.setLevel('debug');
                  scope.setExtras({
                    post,
                    divisions: this.divisions,
                  });

                  captureException(error);
                });

                return;
              }

              captureException(error);
            })
            .finally(() => {
              this.loading = false;
              this.$eventBus.emit('tml-breadcrumbs-update');
            });
        }
      },
    },
  },

  mounted() {
    this.intersectionObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (this.post && entry.intersectionRatio > 0) {
            this.scrolledToBottom = true;
            this.markBlogAsRead();
          }
        });
      },
      {
        rootMargin: '500px',
        trackVisibility: false,
        threshold: [0.0],
      }
    );

    this.intersectionObserver.observe(this.$refs.endOfPostMarker);
  },

  created() {
    this.setMetaData();
  },

  methods: {
    markBlogAsRead() {
      if (
        this.twentySecondsElapsed &&
        this.scrolledToBottom &&
        !isbot(navigator.userAgent)
      ) {
        this.$store.dispatch('blog/logPostRead', this.post);
      }
    },

    loadComments() {
      this.showComments = true;

      this.axios
        .get(formatUrl(`/omnis/v3/blog/comments/${this.post.id}/`))
        .success(commentData => {
          const comments = commentData.map(
            comment => new ChatMessageModel(comment)
          );

          this.commentablePost = Blog.map({...this.post, comments});

          this.markupComments = JSON.stringify(
            this.commentablePost
              .getVisibleCommentsWithReplies()
              .map(comment => ({
                author: {
                  '@type': 'Person',
                  name: comment.owner?.username,
                },
                text: escapeJSON(comment.body.replace(/(<([^>]+)>)/gi, '')),
                dateCreated: new Date(comment.createdAt),
              }))
          );
        })
        .oops();
    },

    goToCommentsSection() {
      scrollToElement(document.querySelector('#comments'), 'top');
    },

    setMetaData() {
      useHead(
        computed(() => {
          let meta = {
            meta: [],
          };

          if (this.post) {
            const host = `${location.protocol}//${location.hostname}`;

            meta = {
              title: this.post.metaTitle ? this.post.metaTitle : this.title,
              script: [
                {
                  type: 'application/ld+json',
                  children: `
              {
                "@context": "http://schema.org/",
                "@type": "NewsArticle",
                "headline": "${escapeJSON(this.post.title)}",
                "name": "${escapeJSON(this.post.title)}",
                "mainEntityOfPage": "${host}${this.$route.fullPath}",
                "url": "${this.post.fullUrl}",
                "description": "${escapeJSON(this.post.description)}",
                "datePublished": "${new Date(this.post.publishedAt * 1000)}",
                "dateModified": "${new Date(this.post.updatedAt * 1000)}",
                "author": {
                  "@type": "Organization",
                  "name": "${
                    this.$store.state.subdomain
                      ? this.$store.state.subdomain.company.name
                      : 'Teemill Tech Ltd'
                  }"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "${
                    this.$store.state.subdomain
                      ? this.$store.state.subdomain.company.name
                      : 'Teemill Tech Ltd'
                  }",
                  "logo": {
                    "@type": "ImageObject",
                    "url": "${
                      this.$store.state.subdomain
                        ? this.$store.state.subdomain.company.logo
                        : this.teemillMetaLogo
                    }"
                  }
                },
                "image": {
                  "@type": "ImageObject",
                  "url": "${this.post.image.size('banner')}"
                },
                "keywords": "${[
                  this.seoKeywords('Tag'),
                  this.seoKeywords('Topic'),
                ]}",
                "commentCount": ${this.commentCount},
                "comment": ${this.markupComments}
              }
            `,
                },
              ],
              meta: [
                {
                  vmid: 'description',
                  name: 'description',
                  content: this.post.metaDescription
                    ? this.post.metaDescription
                    : this.post.description,
                },

                {
                  vmid: 'og:url',
                  property: 'og:url',
                  content: this.post.fullUrl,
                },
                {
                  vmid: 'og:title',
                  property: 'og:title',
                  content: this.post.metaTitle
                    ? this.post.metaTitle
                    : this.title,
                },
                {
                  vmid: 'og:description',
                  property: 'og:description',
                  content: this.post.metaDescription
                    ? this.post.metaDescription
                    : this.post.description,
                },
                {
                  vmid: 'og:image',
                  property: 'og:image',
                  content: this.post.image.size('banner'),
                },
                {
                  vmid: 'og:image:width',
                  property: 'og:image:width',
                  content: '640',
                },
                {
                  vmid: 'og:image:height',
                  property: 'og:image:height',
                  content: '674',
                },
                {vmid: 'og:type', property: 'og:type', content: 'website'},

                {
                  vmid: 'twitter:title',
                  property: 'twitter:title',
                  content: this.post.metaTitle
                    ? this.post.metaTitle
                    : this.title,
                },
                {
                  vmid: 'twitter:card',
                  property: 'twitter:card',
                  content: 'photo',
                },
                {
                  vmid: 'twitter:image',
                  property: 'twitter:image',
                  content: this.post.image.size('banner'),
                },
              ],
              link: [
                {vmid: 'canonical', rel: 'canonical', href: this.post.fullUrl},
              ],
            };

            if (!this.post.showOnSitemap) {
              meta.meta.push({name: 'robots', content: 'noindex'});
            }
          }

          return meta;
        })
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.break {
  margin: auto auto 3rem auto;
}

.footer {
  max-width: 700px;
  margin: auto auto 5vh auto;
}

hr {
  margin: 20px 0;
}
</style>
